<template>
  <div class="table-layout">
    <div v-if="!loading">
      <b-field grouped group-multiline class="actions-field">
        <b-checkbox class="check-all" v-model="selectAll"></b-checkbox>
        <actions>
          <template>
            <slot name="actions"></slot>
          </template>
        </actions>

        <b-upload v-if="addNewUpload" @input="uploadZip($event, addNewUpload)">
          <a class="button is-link">{{ $t('button.upload.zip') }}</a>
        </b-upload>
        <edit-button v-else :editOptions="addOptions" @click="addNew('')">
          <template #edit-actions>
            <b-dropdown-item aria-role="listitem" v-for="(value, key) in addOptions" :key="key" @click="addNew(value)">
              {{ value }}
            </b-dropdown-item>
          </template>
          <template v-if="addNewName" #default>
            {{ addNewName }}
          </template>
        </edit-button>
        <b-upload class="blue-upload" v-if="uploadZipBlue" @input="uploadZip($event, uploadZipBlue)">
          <a class="button is-link">{{ $t('button.upload.zip') }}</a>
        </b-upload>
      </b-field>
      <b-table :data="items">
        <template slot-scope="props">
          <b-table-column class="column-checkbox">
            <label class="b-checkbox checkbox check-all">
              <input type="checkbox" class="check" v-model="selected" :value="$lodash.get(props.row, pkField)" />
              <span class="check"></span>
              <span class="control-label"></span>
            </label>
          </b-table-column>

          <b-table-column
            v-for="(value, key) in columns"
            :key="`${key}-${value.label}`"
            :field="value.field"
            :label="$t(value.label)"
            :class="value.class"
            :sortable="value.sortable"
          >
            <div v-if="value.route">
              <router-link
                v-if="!(value.loadable && $lodash.get(props.row, 'loading'))"
                :to="getRouterLinkParams(value.route, props.row)"
              >
                {{ $lodash.get(props.row, value.field) }}
              </router-link>
              <loader v-else></loader>
            </div>
            <div v-else-if="value.modal">
              <a @click="$emit('open-modal', { modal: value.modal, value: props.row })">
                {{ $lodash.get(props.row, value.field) }}
              </a>
            </div>
            <div v-else-if="value.bindHtml">
              <p
                v-if="!(value.loadable && $lodash.get(props.row, 'loading'))"
                v-html="$lodash.get(props.row, value.field)"
              ></p>
              <loader v-else></loader>
            </div>
            <div v-else-if="value.image">
              <img
                v-if="!(value.loadable && $lodash.get(props.row, 'loading'))"
                :src="$lodash.get(props.row, value.field)"
                class="table-image"
              />
              <loader v-else></loader>
            </div>
            <div v-else-if="value.slot">
              <slot :name="value.slot" :value="props.row"></slot>
            </div>
            <div v-else>
              <p v-if="!(value.loadable && $lodash.get(props.row, 'loading'))">
                {{ $lodash.get(props.row, value.field) }}
              </p>
              <loader v-else>{{ $lodash.get(props.row, value.field) }}</loader>
            </div>
          </b-table-column>
        </template>
        <template slot="empty">
          <div class="content has-text-grey has-text-centered">
            <p>{{ $t('lists.empty') }}</p>
          </div>
        </template>
      </b-table>
    </div>
    <loader v-else></loader>
  </div>
</template>

<script>
import Loader from '@/components/Loader'
import Actions from '@/components/Actions'
import EditButton from '@/components/EditButton'
import uploadZip from '@/api/uploadZip'
export default {
  name: 'TableLayout',
  components: {
    Loader,
    Actions,
    EditButton
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default() {
        return []
      }
    },
    columns: {
      type: Array,
      default() {
        return []
      }
    },
    addOptions: {
      type: Array,
      default() {
        return []
      }
    },
    pkField: {
      type: String,
      default: ''
    },
    addNewUpload: {
      type: String,
      default: ''
    },
    uploadZipBlue: {
      type: String,
      default: ''
    },
    addNewName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      checkboxPosition: 'left',
      active: false,
      selected: []
    }
  },
  computed: {
    selectAll: {
      get: function() {
        return this.items ? this.selected.length == this.items.length : false
      },
      set: function(value) {
        var selected = []
        const pkField = this.pkField
        if (value) {
          this.items.forEach(function(item) {
            selected.push(item[pkField])
          })
        }
        this.selected = selected
      }
    }
  },
  methods: {
    uploadZip(...args) {
      uploadZip.apply(this, args).then(data => {
        this.$emit('uploaded', data)
      })
    },
    addNew(value) {
      this.$emit('click:addNew', value)
    },
    getRouterLinkParams(route, row) {
      let params = {}
      let query = {}
      if (route.isSpecialRowsLinks && this.$lodash.get(row, route.specialField) === route.specialValue) {
        if (route.specialParams) {
          route.specialParams.forEach(param => {
            params[param.name] = row[param.field]
          })
        }
        if (route.specialQuery) {
          route.specialQuery.forEach(q => {
            query[q.name] = row[q.field]
          })
        }
        return {
          name: route.specialName,
          params,
          query
        }
      }
      if (route.params) {
        route.params.forEach(param => {
          params[param.name] = row[param.field]
        })
      }
      if (route.query) {
        route.query.forEach(q => {
          query[q.name] = row[q.field]
        })
      }
      return {
        name: route.name,
        params: params,
        query: query
      }
    },
    selectedChange() {
      this.$emit('update:selected', this.selected)
    }
  },
  watch: {
    selected: {
      immediate: true,
      handler: 'selectedChange'
    }
  }
}
</script>

<style lang="scss">
.table-layout {
  .b-table {
    th.checkbox-cell .checkbox {
      display: none;
    }
    td {
      vertical-align: middle;
      .checkbox {
        display: flex;
      }
    }
    .column-checkbox {
      width: 2.5rem;
    }
    .table-image {
      width: auto;
      height: 10rem;
      object-fit: contain;
    }
  }
  .actions-field {
    display: block;
    .check-all {
      padding: 0.5em 0.8em;
    }
    .dropdown {
      padding: 0.5em 0em;
    }
    .edit-button {
      display: inline;
      float: right;
      @media screen and (max-width: 600px) {
        float: unset;
      }
    }
    .upload.control {
      display: inline;
      height: 1.25rem;
      font-size: 0.9em;
      margin: 0;
      margin-top: 0.5rem;
      float: right;
      @media screen and (max-width: 600px) {
        float: unset;
      }
      .button {
        padding: 0;
        height: 100%;
        padding-right: 1.25rem;
        padding-left: 1.25rem;
        color: white;
        background-color: #4bbd4d;
        font-weight: bold;
      }
    }

    .upload.control.blue-upload {
      clear: unset;
      margin-right: 1rem;
      .button {
        color: #3273dc;
        background-color: #fff;
        border: 1px solid #3273dc;
      }
    }
  }
}
</style>
